/**
 * ImageZoom — click any image of the user to see it as large as the screen allows.
 *
 * ── Why this, and not an Amazon-style hover magnifier ──────────────────────
 *
 * The operator asked for hover-zoom "like on amazon the product image". The
 * goal behind it is right and the mechanism is not, for two reasons worth
 * writing down so this does not get re-proposed:
 *
 *  1. A magnifier answers "what does this material look like up close", which is
 *     Amazon's question about a fabric or a connector. The question here is "is
 *     that me", and that is answered by the WHOLE frame at size: the face, the
 *     posture and the light together. A 2x window over one part of a portrait
 *     shows a nose and hides the answer.
 *  2. Hover does not exist on a phone. Telegram is a hook and the premium
 *     visuals must be experienced in the web app (CLAUDE.md), and a large part
 *     of that app is met on a phone. A control that is unreachable on touch is
 *     not a control.
 *
 * So: the tile stays a tile, and a click opens the image at up to the full
 * viewport. It works with a finger, with a mouse and with a keyboard, and it
 * shows the thing the person is actually trying to judge.
 *
 * Built on the existing Dialog primitive rather than a hand-rolled overlay, so
 * Escape, the focus trap, the scroll lock and the restored focus on close all
 * come from Radix and are not re-implemented (badly) here. The card chrome is
 * stripped: no border, no padding, no background, because anything around the
 * image at this size is a frame competing with the photograph.
 *
 * `srcSet` is forwarded when given. The zoomed view asks for the largest
 * allowlisted width via `sizes="100vw"`, which is the one place that width is
 * genuinely wanted.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface ImageZoomProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  /** Classes for the inline (unzoomed) image. */
  className?: string;
  /** Rendered over the inline image, e.g. a selection badge. */
  overlay?: ReactNode;
  /**
   * Fired when the image is opened at full size.
   *
   * Added 2026-08-16 for the gallery's `anchor.visualized` signal, which used
   * to hang off a button labelled "Visualisieren" whose entire body was the
   * telemetry call. From the user's side that button promised something and
   * did nothing. Opening the image IS the act being measured, so the
   * instrument now sits on it.
   */
  onOpen?: () => void;
}

export function ImageZoom({
  src,
  srcSet,
  sizes,
  alt,
  className,
  overlay,
  onOpen,
}: ImageZoomProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            onOpen?.();
          }}
          aria-label={t('app.image.zoom')}
          data-testid="image-zoom-trigger"
          // `block` and `w-full` so the button is exactly the image and adds no
          // layout of its own: every caller sized the <img> before this existed.
          className="block w-full cursor-zoom-in rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <img src={src} srcSet={srcSet} sizes={sizes} alt={alt} className={cn(className)} />
        </button>
        {overlay}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-none border-0 bg-transparent p-0 shadow-none sm:max-w-none"
          data-testid="image-zoom-dialog"
        >
          {/* Radix requires a title for the accessible name; it is not a caption
              anyone needs to read, so it is visually hidden rather than absent. */}
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <img
            src={src}
            srcSet={srcSet}
            sizes="100vw"
            alt={alt}
            className="mx-auto max-h-[92dvh] w-auto max-w-full rounded-xl object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
